import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { firebaseApp, getCurrentFirebaseIdToken } from './firebase';
import { getSharedFirestore } from './firestoreClient';

const lsKey = (uid) => `rbm_fs_pro_${uid}`;

/** Firestore / JSON bridges sometimes deliver non-strict booleans for flags. */
const isProFlagTruthy = (raw) => (
  raw === true
  || raw === 1
  || raw === '1'
  || String(raw).toLowerCase() === 'true'
);

let entitlementUnsub = null;

const parseExpires = (raw) => {
  if (raw == null) return null;
  if (typeof raw?.toMillis === 'function') {
    const ms = raw.toMillis();
    return Number.isFinite(ms) ? ms : null;
  }
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  const t = Date.parse(String(raw));
  return Number.isFinite(t) ? t : null;
};

const getFirestoreProjectId = () => {
  try {
    return String(process.env.REACT_APP_FIREBASE_PROJECT_ID || '').trim();
  } catch {
    return '';
  }
};

const parseFirestoreValue = (v) => {
  if (!v || typeof v !== 'object') return null;
  if ('booleanValue' in v) return Boolean(v.booleanValue);
  if ('stringValue' in v) return String(v.stringValue);
  if ('integerValue' in v) return Number(v.integerValue);
  if ('doubleValue' in v) return Number(v.doubleValue);
  if ('timestampValue' in v) return String(v.timestampValue);
  if ('nullValue' in v) return null;
  return null;
};

const parseFirestoreRestDoc = (restDoc) => {
  const fields = restDoc?.fields && typeof restDoc.fields === 'object' ? restDoc.fields : {};
  return {
    proSubscriptionActive: parseFirestoreValue(fields.proSubscriptionActive),
    subscriptionTier: parseFirestoreValue(fields.subscriptionTier),
    proSubscriptionExpiresAt: parseFirestoreValue(fields.proSubscriptionExpiresAt),
  };
};

const fetchEntitlementViaFirestoreRest = async (uid) => {
  const projectId = getFirestoreProjectId();
  if (!projectId) throw new Error('Missing REACT_APP_FIREBASE_PROJECT_ID for REST fallback');
  const idToken = await getCurrentFirebaseIdToken();
  if (!idToken) throw new Error('No Firebase ID token for REST fallback');
  const url = `https://firestore.googleapis.com/v1/projects/${projectId}/databases/(default)/documents/users/${encodeURIComponent(uid)}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${idToken}`,
    },
  });
  if (res.status === 404) return { exists: false, data: {} };
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`REST ${res.status}: ${body || 'failed to read users doc'}`);
  }
  const json = await res.json();
  return { exists: true, data: parseFirestoreRestDoc(json) };
};

const persistEntitlementPayload = (uid, payload) => {
  if (!uid || typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(lsKey(uid), JSON.stringify(payload));
  } catch {
    /* ignore */
  }
  try {
    window.dispatchEvent(new CustomEvent('rebuildme-entitlement-changed'));
  } catch {
    /* ignore */
  }
};

const deriveEntitlementFromDocData = (d) => {
  const src = d && typeof d === 'object' ? d : {};
  const tier = String(src.subscriptionTier || '').trim();
  const active = isProFlagTruthy(src.proSubscriptionActive) && tier.toLowerCase() === 'pro';
  const expiresAtMs = parseExpires(src.proSubscriptionExpiresAt);
  return { active, expiresAtMs, tier };
};

/**
 * Cached snapshot of `users/{uid}` fields used for manual Pro grants:
 * proSubscriptionActive (bool), subscriptionTier (string, e.g. "pro"),
 * optional proSubscriptionExpiresAt (Timestamp | ISO string | ms).
 */
export function readCachedFirestorePro(uid) {
  if (!uid || typeof localStorage === 'undefined') {
    return { active: false, expiresAtMs: null, tier: '' };
  }
  try {
    const raw = localStorage.getItem(lsKey(uid));
    if (!raw) return { active: false, expiresAtMs: null, tier: '' };
    const o = JSON.parse(raw);
    const tier = String(o?.tier || '').trim();
    const active = isProFlagTruthy(o?.active) && tier.toLowerCase() === 'pro';
    return {
      active,
      expiresAtMs: o?.expiresAtMs != null && Number.isFinite(Number(o.expiresAtMs)) ? Number(o.expiresAtMs) : null,
      tier,
    };
  } catch {
    return { active: false, expiresAtMs: null, tier: '' };
  }
}

export function isFirestoreProEntitlementActive(uid) {
  const { active, expiresAtMs, tier } = readCachedFirestorePro(uid);
  if (!active || String(tier).toLowerCase() !== 'pro') return false;
  if (expiresAtMs != null && expiresAtMs < Date.now()) return false;
  return true;
}

export function stopUserFirestoreEntitlementListener() {
  if (typeof entitlementUnsub === 'function') {
    try {
      entitlementUnsub();
    } catch {
      /* ignore */
    }
    entitlementUnsub = null;
  }
}

/**
 * Live updates when `users/{uid}` changes in Firestore (manual Pro grants, expiry edits).
 * Call again after uid changes; stops any prior listener.
 */
export function startUserFirestoreEntitlementListener(uid) {
  stopUserFirestoreEntitlementListener();
  if (!uid || !firebaseApp || typeof localStorage === 'undefined') return;
  try {
    const db = getSharedFirestore(firebaseApp);
    if (!db) return;
    entitlementUnsub = onSnapshot(
      doc(db, 'users', uid),
      (snap) => {
        if (!snap.exists()) {
          try {
            localStorage.removeItem(lsKey(uid));
          } catch {
            /* ignore */
          }
          try {
            window.dispatchEvent(new CustomEvent('rebuildme-entitlement-changed'));
          } catch {
            /* ignore */
          }
          return;
        }
        const payload = deriveEntitlementFromDocData(snap.data() || {});
        persistEntitlementPayload(uid, payload);
      },
      (err) => {
        console.warn('[userFirestoreEntitlement] snapshot error:', err?.message || err);
      },
    );
  } catch (e) {
    console.warn('[userFirestoreEntitlement] listener start failed:', e?.message || e);
  }
}

export async function refreshUserFirestoreEntitlement(uid) {
  if (!uid) return readCachedFirestorePro('');
  if (!firebaseApp || typeof localStorage === 'undefined') {
    return readCachedFirestorePro(uid);
  }
  try {
    const db = getSharedFirestore(firebaseApp);
    if (!db) return readCachedFirestorePro(uid);

    const GET_DOC_TIMEOUT_MS = 8000;
    const snap = await Promise.race([
      getDoc(doc(db, 'users', uid)),
      new Promise((_, reject) => {
        window.setTimeout(() => reject(new Error(`getDoc timeout after ${GET_DOC_TIMEOUT_MS}ms`)), GET_DOC_TIMEOUT_MS);
      }),
    ]);

    if (!snap.exists()) {
      try {
        localStorage.removeItem(lsKey(uid));
      } catch {
        /* ignore */
      }
      try {
        window.dispatchEvent(new CustomEvent('rebuildme-entitlement-changed'));
      } catch {
        /* ignore */
      }
      return { active: false, expiresAtMs: null, tier: '' };
    }

    const payload = deriveEntitlementFromDocData(snap.data() || {});
    persistEntitlementPayload(uid, payload);
    return payload;
  } catch (e) {
    try {
      const rest = await fetchEntitlementViaFirestoreRest(uid);
      if (!rest.exists) {
        try {
          localStorage.removeItem(lsKey(uid));
        } catch {
          /* ignore */
        }
        return { active: false, expiresAtMs: null, tier: '' };
      }
      const payload = deriveEntitlementFromDocData(rest.data || {});
      persistEntitlementPayload(uid, payload);
      return payload;
    } catch (restErr) {
      console.warn('[userFirestoreEntitlement] refresh fallback failed:', restErr?.message || restErr);
    }
    console.warn('[userFirestoreEntitlement] refresh failed:', e?.message || e);
    return readCachedFirestorePro(uid);
  }
}
